import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { GridValidRowModel } from "@mui/x-data-grid";
import { SweepDestStatus } from "@wallet-manager/node-types/dist/src/postgres/const";
import { Dispatch, SetStateAction } from "react";
import MpTextFieldNumberOnly from "src/components/MpTextFieldNumberOnly";
import { Box, Checkbox, Typography } from "src/components/MuiGenerals";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import LayoutRow from "src/views/WalletManagement/TronWalletResourceBalance/components/LayoutRow";
import { EditGroupFields } from "../types/EditGroupFields";
import { GetListRes } from "../types/GetListRes";
import { TableColumnKey } from "../types/Table";

type Props = {
  selectedRow?: GridValidRowModel & { rawData: GetListRes };
  editGroupFields?: EditGroupFields;
  setEditGroupFields: Dispatch<SetStateAction<EditGroupFields | undefined>>;
};

const EditRecordDialogContent = (props: Props) => {
  const { selectedRow, editGroupFields, setEditGroupFields } = props;
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, tc, te } = useTranslation(translatePrefix);

  return (
    <Box
      sx={{
        margin: "8px 0",
        "& .MuiFormControl-root": {
          width: "100%",
        },
      }}
      display={"flex"}
      flexDirection={"column"}
      gap={1}
    >
      <LayoutRow
        title={t(TableColumnKey.ChainName)}
        content={
          <Typography style={{ textAlign: "right" }}>
            {selectedRow?.[TableColumnKey.ChainName]}
          </Typography>
        }
      />
      <LayoutRow
        title={t(TableColumnKey.Currency)}
        content={
          <Typography style={{ textAlign: "right" }}>
            {selectedRow?.[TableColumnKey.Currency]}
          </Typography>
        }
      />
      <LayoutRow
        title={t(TableColumnKey.GroupName)}
        content={
          <Typography style={{ textAlign: "right" }}>
            {selectedRow?.[TableColumnKey.GroupName]}
          </Typography>
        }
      />
      <LayoutRow
        title={t(TableColumnKey.GroupType)}
        content={
          <Typography style={{ textAlign: "right" }}>
            {selectedRow?.[TableColumnKey.GroupType]}
          </Typography>
        }
      />
      <Typography variant="h6">{t(TableColumnKey.GroupSequence)}</Typography>
      <MpTextFieldNumberOnly
        label={tc("phInputField", {
          fieldName: t(TableColumnKey.GroupSequence),
        })}
        value={editGroupFields?.groupSequence}
        onChange={(e) => {
          setEditGroupFields((f) => ({
            ...f,
            groupSequence: e.target.value,
          }));
        }}
      />
      <LayoutRow
        title={t(TableColumnKey.Status)}
        content={
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onChange={(e) => {
                setEditGroupFields((f) => ({ ...f, status: e.target.value }));
              }}
              value={editGroupFields?.status ?? ""}
            >
              <FormControlLabel
                value={SweepDestStatus.Enable}
                control={<Radio />}
                label={te("enable")}
              />
              <FormControlLabel
                value={SweepDestStatus.Disable}
                sx={{ marginRight: 0 }}
                control={<Radio />}
                label={te("disable")}
              />
            </RadioGroup>
          </Box>
        }
      />

      <Typography variant="h6">
        {t(TableColumnKey.RotateAmountLimit)}
      </Typography>
      <MpTextFieldNumberOnly
        label={tc("phInputField", {
          fieldName: t(TableColumnKey.RotateAmountLimit),
        })}
        value={editGroupFields?.rotateAmountLimit}
        disabled={editGroupFields?.unlimitAmount}
        onChange={(e) => {
          setEditGroupFields((f) => ({
            ...f,
            rotateAmountLimit: e.target.value,
          }));
        }}
      />

      <Typography variant="h6">
        {t(TableColumnKey.MaximumAmountLimit)}
      </Typography>
      <MpTextFieldNumberOnly
        label={tc("phInputField", {
          fieldName: t(TableColumnKey.MaximumAmountLimit),
        })}
        value={editGroupFields?.maximumAmountLimit}
        disabled={editGroupFields?.unlimitAmount}
        onChange={(e) => {
          setEditGroupFields((f) => ({
            ...f,
            maximumAmountLimit: e.target.value,
          }));
        }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={editGroupFields?.unlimitAmount ?? false}
            onChange={(e) => {
              setEditGroupFields((f) => ({
                ...f,
                unlimitAmount: e.target.checked,
              }));
            }}
          />
        }
        label={t("unlimitAmount")}
      />
    </Box>
  );
};

export default EditRecordDialogContent;
